import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Badge,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
} from "@asm/ui";
import {
  Plus,
  Search,
  Trash2,
  Edit,
  ShieldOff,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  exceptionsApi,
  type AssetException,
  type CreateExceptionParams,
} from "@/lib/api";

const SCAN_MODULES = [
  "dns",
  "email",
  "whois",
  "tls",
  "headers",
  "tech",
  "cloud",
  "cloud_misconfig",
  "asn",
  "repos",
  "crawl_hints",
  "js_analysis",
  "favicon",
  "typosquat",
  "portscan",
  "service_probe",
  "subdomains_bruteforce",
  "ipintel",
  "vuln",
];

export default function Exceptions() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editException, setEditException] = useState<AssetException | null>(
    null
  );
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const perPage = 25;

  const { data, isLoading } = useQuery({
    queryKey: ["exceptions", search, typeFilter, page],
    queryFn: () =>
      exceptionsApi.list({
        search: search || undefined,
        asset_type: typeFilter === "all" ? undefined : typeFilter,
        page,
        per_page: perPage,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => exceptionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exceptions"] });
      setDeleteId(null);
    },
  });

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Asset Exceptions</h2>
          <p className="text-sm text-muted-foreground">
            Assets marked as not owned by your organization, excluded from scan
            processing.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Exception
            </Button>
          </DialogTrigger>
          <CreateExceptionDialog
            onClose={() => setCreateOpen(false)}
            onCreated={() => {
              setCreateOpen(false);
              queryClient.invalidateQueries({ queryKey: ["exceptions"] });
            }}
          />
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by identifier..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>
        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Asset type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="subdomain">Subdomain</SelectItem>
            <SelectItem value="ip">IP</SelectItem>
            <SelectItem value="port">Port</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results */}
      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading exceptions...
          </CardContent>
        </Card>
      ) : !data?.items.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldOff className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">
              No asset exceptions configured.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Mark assets as not owned from the asset detail page, or add
              exceptions manually.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {data.total} exception{data.total !== 1 ? "s" : ""}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {data.items.map((ex) => (
                <div
                  key={ex.id}
                  className="flex items-center justify-between px-6 py-3 hover:bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs shrink-0">
                        {ex.asset_type}
                      </Badge>
                      <span className="font-mono text-sm truncate">
                        {ex.identifier}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {ex.excluded_modules.length === 0 ? (
                        <Badge
                          variant="destructive"
                          className="text-xs"
                        >
                          All modules
                        </Badge>
                      ) : (
                        ex.excluded_modules.slice(0, 4).map((m) => (
                          <Badge
                            key={m}
                            variant="secondary"
                            className="text-xs"
                          >
                            {m}
                          </Badge>
                        ))
                      )}
                      {ex.excluded_modules.length > 4 && (
                        <span className="text-xs text-muted-foreground">
                          +{ex.excluded_modules.length - 4} more
                        </span>
                      )}
                      {ex.reason && (
                        <span className="text-xs text-muted-foreground ml-2 truncate max-w-[200px]">
                          {ex.reason}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setEditException(ex)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteId(ex.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Exception</DialogTitle>
            <DialogDescription>
              This asset will be included in future scans again. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Removing..." : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      {editException && (
        <EditExceptionDialog
          exception={editException}
          open={!!editException}
          onClose={() => setEditException(null)}
          onUpdated={() => {
            setEditException(null);
            queryClient.invalidateQueries({ queryKey: ["exceptions"] });
          }}
        />
      )}
    </div>
  );
}

function CreateExceptionDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [assetType, setAssetType] = useState("subdomain");
  const [identifier, setIdentifier] = useState("");
  const [reason, setReason] = useState("");
  const [excludeAll, setExcludeAll] = useState(true);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const createMutation = useMutation({
    mutationFn: (data: CreateExceptionParams) => exceptionsApi.create(data),
    onSuccess: onCreated,
  });

  const handleSubmit = () => {
    if (!identifier.trim()) return;
    createMutation.mutate({
      asset_type: assetType,
      identifier: identifier.trim(),
      excluded_modules: excludeAll ? [] : selectedModules,
      reason: reason.trim() || undefined,
    });
  };

  const toggleModule = (mod: string) => {
    setSelectedModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]
    );
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Add Asset Exception</DialogTitle>
        <DialogDescription>
          Mark an asset as not owned by your organization. It will be excluded
          from future scan processing.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Asset Type</Label>
            <Select value={assetType} onValueChange={setAssetType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="subdomain">Subdomain</SelectItem>
                <SelectItem value="ip">IP Address</SelectItem>
                <SelectItem value="port">Port</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Identifier</Label>
            <Input
              placeholder={
                assetType === "subdomain"
                  ? "cdn.example.com"
                  : assetType === "ip"
                    ? "1.2.3.4"
                    : "1.2.3.4:443"
              }
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label>Reason (optional)</Label>
          <textarea
            className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="CDN not owned by us, third-party service..."
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
            rows={2}
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              id="exclude-all"
              checked={excludeAll}
              onChange={(e) => setExcludeAll(e.target.checked)}
              className="rounded border-border"
            />
            <Label htmlFor="exclude-all" className="text-sm font-normal">
              Exclude from all modules
            </Label>
          </div>
          {!excludeAll && (
            <div className="flex flex-wrap gap-1.5 p-3 border rounded-md max-h-40 overflow-y-auto">
              {SCAN_MODULES.map((mod) => (
                <Badge
                  key={mod}
                  variant={
                    selectedModules.includes(mod) ? "default" : "outline"
                  }
                  className="cursor-pointer text-xs"
                  onClick={() => toggleModule(mod)}
                >
                  {mod}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            !identifier.trim() ||
            (!excludeAll && selectedModules.length === 0) ||
            createMutation.isPending
          }
        >
          {createMutation.isPending ? "Creating..." : "Create Exception"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditExceptionDialog({
  exception,
  open,
  onClose,
  onUpdated,
}: {
  exception: AssetException;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [reason, setReason] = useState(exception.reason || "");
  const [excludeAll, setExcludeAll] = useState(
    exception.excluded_modules.length === 0
  );
  const [selectedModules, setSelectedModules] = useState<string[]>(
    exception.excluded_modules
  );

  const updateMutation = useMutation({
    mutationFn: () =>
      exceptionsApi.update(exception.id, {
        excluded_modules: excludeAll ? [] : selectedModules,
        reason: reason.trim() || undefined,
      }),
    onSuccess: onUpdated,
  });

  const toggleModule = (mod: string) => {
    setSelectedModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Exception</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{exception.identifier}</span> (
            {exception.asset_type})
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Reason</Label>
            <textarea
              className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={reason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="edit-exclude-all"
                checked={excludeAll}
                onChange={(e) => setExcludeAll(e.target.checked)}
                className="rounded border-border"
              />
              <Label htmlFor="edit-exclude-all" className="text-sm font-normal">
                Exclude from all modules
              </Label>
            </div>
            {!excludeAll && (
              <div className="flex flex-wrap gap-1.5 p-3 border rounded-md max-h-40 overflow-y-auto">
                {SCAN_MODULES.map((mod) => (
                  <Badge
                    key={mod}
                    variant={
                      selectedModules.includes(mod) ? "default" : "outline"
                    }
                    className="cursor-pointer text-xs"
                    onClick={() => toggleModule(mod)}
                  >
                    {mod}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => updateMutation.mutate()}
            disabled={
              (!excludeAll && selectedModules.length === 0) ||
              updateMutation.isPending
            }
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
